import { describe_error } from '../utils/ErrorDescriber';
import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Button, Spinner, Alert, Card, Collapse } from 'react-bootstrap';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';

import SiteAnalysisSummary from '../components/SiteAnalysisSummary';
import AssistantLayout from '../components/AssistantLayout';
import ScreenshotThumbnail from '../components/ScreenshotThumbnail';
import ScoreRowsTable from '../components/ScoreRowsTable';
import PdfExportModal from '../components/PdfExportModal';
import QuotaExhaustedModal from './modals/QuotaExhaustedModal.js';

import { export_site_pdf, stop_site_pdf_polling } from '../utils/SitePdfExport';

import Constants from '../Constants';
import { useAuth } from '../AuthContext';
import { get_user_error_message } from '../utils/ErrorMessages';
import { fetch_analysis_running } from '../utils/AnalysisRunning';

// How often a running site analysis is polled.
const c_StatusPollIntervalMs = 1000;

// The title and line under it, as the page analysis names its tool.
const c_Headline = 'Site AEO Analysis';
const c_Subheadline = 'How well AI search tools can read, understand and quote this site, page by page.';

// How many lines of the Site Notes still show while the section is collapsed.
const c_CollapsedNoteLines = 3;

// The quota modal's copy: no credits to start a site analysis, and credits running out partway through one.
const c_QuotaModalTitle = 'Quota Exhausted';
const c_NoCreditsMessage = 'You’ve used all your free AEO Audited runs for today. You can run another analysis tomorrow';
const c_CreditsRanOutMessage = 'You ran out of analysis credits during this site analysis, so it covers only the pages analyzed before then.';

// The lines of a site note, which the backend joins with new lines.
const get_note_lines = (note_text)=>{
	if(!note_text){
		return [];
	}

	return note_text.split('\n').map((line)=>{ return line.trim(); }).filter((line)=>{ return line.length > 0; });
};


// The site's latest analysis and its pages, or the run named by the route's site_analysis_id when the view was
// opened from the dashboard's run list. on_analysis_change and on_ask_ai come from AssistantLayout: the assistant
// is told which site analysis is on screen, and Ask AI asks it about that analysis.
function SiteViewContent({on_analysis_change, on_ask_ai}){
	const navigate = useNavigate();
	const location = useLocation();
	const { token } = useAuth();
	const { id, site_analysis_id: requested_site_analysis_id } = useParams();

	const [site, set_site] = useState(null);
	// Whether the site and its run on screen have loaded, so a start_analysis request knows the run before it.
	const [site_loaded, set_site_loaded] = useState(false);
	const [site_analysis, set_site_analysis] = useState(null);
	const [analyzing, set_analyzing] = useState(false);
	// Whether the user has an analysis going anywhere, page or site: they run one at a time, so Analyze is disabled.
	const [analysis_running, set_analysis_running] = useState(false);
	// The run being analyzed, once known: the progress shows its count, never an earlier run's.
	const [analyzing_run_id, set_analyzing_run_id] = useState(null);
	const [error_message, set_error_message] = useState('');
	// Collapsed until the user's setting is read, as the setting defaults to collapsed.
	const [show_notes, set_show_notes] = useState(false);
	// The message the quota modal shows; null when it is closed.
	const [exhausted_message, set_exhausted_message] = useState(null);

	// The pending poll, and whether the view is still mounted, so a poll never outlives the view.
	const poll_timeout_ref = useRef(null);
	const mounted_ref = useRef(true);

	// Whether the new run is still being looked for while the Analyze request builds it (see schedule_build_poll).
	const build_polling_ref = useRef(false);

	// The site report export: the PDF Export button on the summary strip, its progress modal and its error, and the
	// export's progress poll (see SitePdfExport).
	const [fetching_pdf, set_fetching_pdf] = useState(false);
	const [pdf_stage, set_pdf_stage] = useState('');
	const [pdf_error_message, set_pdf_error_message] = useState('');
	const pdf_poll_interval_ref = useRef(null);

	const get_headers = ()=>{
		return { Authorization: 'Bearer ' + token };
	};

	useEffect(()=>{
		return ()=>{
			stop_site_pdf_polling(pdf_poll_interval_ref);
		};
	}, []);

	const stop_polling = ()=>{
		if(poll_timeout_ref.current){
			clearTimeout(poll_timeout_ref.current);
			poll_timeout_ref.current = null;
		}
	};

	useEffect(()=>{
		mounted_ref.current = true;
		load_site();
		load_analysis_running();

		return ()=>{
			mounted_ref.current = false;
			stop_polling();
		};
	}, [id, requested_site_analysis_id]);

	// Opened by the dashboard's Analyze (start_analysis): the analysis starts once the site has loaded, and the flag is
	// cleared from the history entry so a reload or Back does not start another.
	useEffect(()=>{
		if(!site_loaded || !location.state || !location.state.start_analysis){
			return;
		}

		navigate(location.pathname, { replace: true, state: null });
		analyze_site();
	}, [site_loaded]);

	// The Site Notes start as the user's App Settings say; collapsed when the settings cannot be read.
	useEffect(()=>{
		const load_notes_setting = async()=>{
			try {
				const response = await axios.get(Constants.SERVER_URL + '/api/app_settings', { headers: get_headers() });
				if(mounted_ref.current && response.data){
					set_show_notes(!response.data.site_notes_collapsed);
				}
			}
			catch (error) {
				console.log('SiteView/load_notes_setting - ERROR: ', describe_error(error));
			}
		};

		load_notes_setting();
	}, []);

	// The assistant follows the run on screen, not each poll's copy of it.
	const site_analysis_id = site_analysis ? site_analysis.id : null;
	useEffect(()=>{
		on_analysis_change(site_analysis, 'site');
	}, [site_analysis_id]);

	const load_analysis_running = async()=>{
		const running = await fetch_analysis_running(token);
		if(mounted_ref.current){
			set_analysis_running(running);
		}
	};

	// Shows a run's status. A run still going is polled again until it finishes or fails.
	const apply_site_analysis_status = (status)=>{
		const status_site_analysis = status.site_analysis;
		set_site_analysis(status_site_analysis);

		if(!status_site_analysis){
			set_analyzing(false);
			load_analysis_running();
			return;
		}

		if(status_site_analysis.stage == 'failed'){
			set_analyzing(false);
			set_error_message('The site analysis could not be completed. Please try again.');
			load_analysis_running();
			return;
		}

		if(status.done){
			set_analyzing(false);
			load_analysis_running();
			return;
		}

		set_analyzing(true);
		set_analyzing_run_id(status_site_analysis.id);
		schedule_poll(status_site_analysis.id);
	};

	// While the Analyze request builds the new run, the site's latest run is read so its count shows as it grows. A
	// run other than previous_site_analysis_id is the new one.
	const schedule_build_poll = (previous_site_analysis_id)=>{
		stop_polling();

		poll_timeout_ref.current = setTimeout(async()=>{
			poll_timeout_ref.current = null;

			try {
				const response = await axios.get(Constants.SERVER_URL + '/api/latest_site_analysis/' + id, { headers: get_headers() });
				const latest_site_analysis = response.data ? response.data.site_analysis : null;
				if(mounted_ref.current && build_polling_ref.current && latest_site_analysis && (latest_site_analysis.id !== previous_site_analysis_id)){
					set_analyzing_run_id(latest_site_analysis.id);
					set_site_analysis(latest_site_analysis);
				}
			}
			catch (error) {
				console.log('SiteView/schedule_build_poll - ERROR: ', describe_error(error));
			}

			if(mounted_ref.current && build_polling_ref.current){
				schedule_build_poll(previous_site_analysis_id);
			}
		}, c_StatusPollIntervalMs);
	};

	const stop_build_poll = ()=>{
		build_polling_ref.current = false;
		stop_polling();
	};

	// Each poll is scheduled only after the previous one has finished, so polls never overlap.
	const schedule_poll = (site_analysis_id)=>{
		stop_polling();

		poll_timeout_ref.current = setTimeout(async()=>{
			poll_timeout_ref.current = null;

			try {
				const response = await axios.get(Constants.SERVER_URL + '/api/site_analysis_status/' + site_analysis_id, { headers: get_headers() });
				if(mounted_ref.current){
					apply_site_analysis_status(response.data);
				}
			}
			catch (error) {
				console.log('SiteView/schedule_poll - ERROR: ', describe_error(error));
				if(mounted_ref.current){
					set_analyzing(false);
					set_error_message(get_user_error_message(error, 'The site analysis status could not be loaded.'));
				}
			}
		}, c_StatusPollIntervalMs);
	};

	const load_site = async()=>{
		try {
			set_error_message('');
			set_site_loaded(false);

			const site_response = await axios.get(Constants.SERVER_URL + '/api/site/' + id, { headers: get_headers() });
			if(!mounted_ref.current){
				return;
			}

			if(!site_response.data || !site_response.data.site){
				set_error_message('Site not found.');
				return;
			}

			set_site(site_response.data.site);

			// The run the route names, else the site's latest.
			const status_url = requested_site_analysis_id ?
				(Constants.SERVER_URL + '/api/site_analysis_status/' + requested_site_analysis_id) :
				(Constants.SERVER_URL + '/api/latest_site_analysis/' + id);
			const status_response = await axios.get(status_url, { headers: get_headers() });
			if(mounted_ref.current){
				apply_site_analysis_status(status_response.data);
				set_site_loaded(true);
			}
		}
		catch (error) {
			console.log('SiteView/load_site - ERROR: ', describe_error(error));
			if(mounted_ref.current){
				set_error_message(get_user_error_message(error, 'The site could not be loaded.'));
			}
		}
	};

	const analyze_site = async()=>{
		if(analyzing || !site){
			return;
		}

		try {
			set_error_message('');
			set_analyzing(true);
			set_analyzing_run_id(null);

			build_polling_ref.current = true;
			schedule_build_poll(site_analysis ? site_analysis.id : null);

			const payload = {
				url: site.url,
				user_id: localStorage.getItem(Constants.USER_ID),
				email: localStorage.getItem(Constants.USER_EMAIL),
				browser_uuid: localStorage.getItem('aeoaudited_browser_uuid')
			};

			const response = await axios.post(Constants.SERVER_URL + '/api/run_aeo_site_audit', payload, { headers: get_headers() });
			stop_build_poll();
			if(!mounted_ref.current){
				return;
			}

			const site_analysis_id = response.data ? response.data.site_analysis_id : null;
			if(!site_analysis_id){
				set_analyzing(false);
				set_error_message('No pages were found to analyze for this site.');
				return;
			}

			// The credits ran out partway: the site analysis goes ahead on the pages it has, and the user is told.
			if(response.data.credits_exhausted){
				set_exhausted_message(c_CreditsRanOutMessage);
			}

			set_analyzing_run_id(site_analysis_id);
			schedule_poll(site_analysis_id);
		}
		catch (error) {
			stop_build_poll();
			console.log('SiteView/analyze_site - ERROR: ', describe_error(error));
			if(!mounted_ref.current){
				return;
			}

			set_analyzing(false);
			load_analysis_running();

			if(error.response && (error.response.status === 403)){
				set_exhausted_message(c_NoCreditsMessage);
				return;
			}

			set_error_message(get_user_error_message(error, 'The site analysis could not be started. Please try again.'));
		}
	};

	const open_page = (page_analysis)=>{
		if(page_analysis.site_page_id){
			navigate('/site-page/' + page_analysis.site_page_id);
		}
	};

	const render_analyze_button = ()=>{
		return (
			<Button variant="primary" className="analyze-site-button" disabled={analyzing || analysis_running || !site} onClick={analyze_site}>
				{analyzing ? 'Analyzing...' : 'Analyze'}
			</Button>
		);
	};

	const render_progress = ()=>{
		if(!analyzing){
			return null;
		}

		const is_analyzing_run = (site_analysis !== null) && (analyzing_run_id !== null) && (site_analysis.id === analyzing_run_id);
		const num_pages = (is_analyzing_run && site_analysis.num_pages) ? site_analysis.num_pages : 0;
		const num_finished = (is_analyzing_run && site_analysis.page_analyses_scoring) ? site_analysis.page_analyses_scoring.length : 0;
		const progress_percent = (is_analyzing_run && site_analysis.progress_percent) ? site_analysis.progress_percent : 0;
		const progress_text = (num_pages > 0) ?
			('Analyzing pages: ' + progress_percent + '%, ' + num_finished + ' of ' + num_pages + ' finished') :
			'Starting the site analysis...';

		return (
			<div className="site-analysis-progress">
				<Spinner animation="border" size="sm"/>
				<span className="site-analysis-progress-text">{progress_text}</span>
			</div>
		);
	};

	const on_pdf_export = (event)=>{
		event.preventDefault();
		if(site_analysis){
			export_site_pdf(token, site_analysis.id, {
				set_fetching_pdf,
				set_pdf_stage,
				set_error_message: set_pdf_error_message,
				poll_interval_ref: pdf_poll_interval_ref
			});
		}
	};

	const render_summary = ()=>{
		if(!site_analysis || (site_analysis.stage != 'finished')){
			return null;
		}

		return (
			<SiteAnalysisSummary
				site_analysis={site_analysis}
				on_ask_ai={on_ask_ai}
				on_pdf_export={on_pdf_export}
				analyzing={analyzing}
				fetching_pdf={fetching_pdf}/>
		);
	};

	const render_note_section = (title, note_text)=>{
		const lines = get_note_lines(note_text);
		if(lines.length == 0){
			return null;
		}

		return (
			<div className="site-notes-section">
				<h6 className="site-notes-section-title">{title}</h6>
				<ul className="site-notes-list">
					{lines.map((line, line_index)=>{ return <li key={line_index}>{line}</li>; })}
				</ul>
			</div>
		);
	};

	// The collapsed notes: their first c_CollapsedNoteLines lines, under their section titles, faded out at the bottom,
	// so the user can see there are notes to open.
	const render_notes_preview = (note_sections)=>{
		let lines_left = c_CollapsedNoteLines;
		const preview_sections = [];
		for(var section_index = 0; (section_index < note_sections.length) && (lines_left > 0); ++section_index){
			const lines = get_note_lines(note_sections[section_index].note_text).slice(0, lines_left);
			if(lines.length > 0){
				preview_sections.push({ title: note_sections[section_index].title, lines });
				lines_left -= lines.length;
			}
		}

		return (
			<div className="site-notes-preview">
				{preview_sections.map((preview_section)=>{
					return (
						<div key={preview_section.title} className="site-notes-section">
							<h6 className="site-notes-section-title">{preview_section.title}</h6>
							<ul className="site-notes-list">
								{preview_section.lines.map((line, line_index)=>{ return <li key={line_index}>{line}</li>; })}
							</ul>
						</div>
					);
				})}
			</div>
		);
	};

	const render_notes = ()=>{
		if(!site_analysis || (site_analysis.stage != 'finished') || !site_analysis.scoring){
			return null;
		}

		const scoring = site_analysis.scoring;
		const has_notes = (get_note_lines(scoring.site_depth_notes).length > 0) ||
			(get_note_lines(scoring.site_strengths).length > 0) ||
			(get_note_lines(scoring.site_weaknesses).length > 0);

		if(!has_notes){
			return null;
		}

		const note_sections = [
			{ title: 'Content Depth', note_text: scoring.site_depth_notes },
			{ title: 'Strengths', note_text: scoring.site_strengths },
			{ title: 'Weaknesses', note_text: scoring.site_weaknesses }
		];

		// The notes collapse and expand from their header, as the page analysis sections do, with the triangle at the
		// header's right. Collapsed, their first lines still show.
		return (
			<Card className="site-notes-card">
				<div className="site-notes-header" onClick={()=>{ set_show_notes(!show_notes); }}>
					<h5 className="site-notes-title">Site Notes</h5>
					<span className="site-notes-toggle">{show_notes ? '▲' : '▼'}</span>
				</div>
				{!show_notes && render_notes_preview(note_sections)}
				<Collapse in={show_notes}>
					<div>
						{note_sections.map((note_section)=>{
							return (
								<React.Fragment key={note_section.title}>
									{render_note_section(note_section.title, note_section.note_text)}
								</React.Fragment>
							);
						})}
					</div>
				</Collapse>
			</Card>
		);
	};

	// The run's pages as table rows: the page url without its scheme, its grade and its scores, in the order the
	// backend sends them, best grade first.
	const get_page_rows = ()=>{
		if(!site_analysis || !site_analysis.page_analyses_scoring){
			return [];
		}

		return site_analysis.page_analyses_scoring.map((page_analysis)=>{
			return {
				id: page_analysis.id,
				label: page_analysis.url.replace('https://', '').replace('http://', ''),
				grade: page_analysis.grade,
				scoring: page_analysis.scoring,
				site_page_id: page_analysis.site_page_id
			};
		});
	};

	const render_pages = ()=>{
		return (
			<ScoreRowsTable rows={get_page_rows()} label_header="Page" on_open={open_page} sortable={true}/>
		);
	};

	const render_exhausted_modal = ()=>{
		if(!exhausted_message){
			return null;
		}

		return (
			<QuotaExhaustedModal
				title={c_QuotaModalTitle}
				message={exhausted_message}
				on_close={()=>{ set_exhausted_message(null); }}/>
		);
	};

	// The site's own thumbnail, taken from the live homepage when the site was crawled, else the one this analysis
	// stored when it analyzed the homepage.
	const render_homepage_screenshot = ()=>{
		let image_id = (site && site.screenshot_thumbnail_image_id) ? site.screenshot_thumbnail_image_id : null;
		if(!image_id && site_analysis && site_analysis.screenshot_thumbnail_image_id){
			image_id = site_analysis.screenshot_thumbnail_image_id;
		}

		if(!image_id){
			return null;
		}

		return (
			<div className="site-title-screenshot">
				<ScreenshotThumbnail image_id={image_id} alt_text="Screenshot of the homepage"/>
			</div>
		);
	};

	const render_title = ()=>{
		if(!site){
			return null;
		}

		return (
			<div className="site-title-block">
				<div>
					<a className="site-title site-title-link" href={site.url} target="_blank" rel="noopener noreferrer">
						{site.url.replace('https://', '').replace('http://', '')}
					</a>
					{render_coverage()}
				</div>
				{render_homepage_screenshot()}
			</div>
		);
	};

	const format_run_date = (run_date)=>{
		const date = new Date(run_date);
		return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) +
			' ' + date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
	};

	// How much of the site the run covered, the scores describe only the pages analyzed, and the date the run's page
	// data is from.
	const render_coverage = ()=>{
		if(!site_analysis || (site_analysis.stage != 'finished') || !site_analysis.num_site_pages){
			return null;
		}

		const num_analyzed = site_analysis.page_analyses_scoring ? site_analysis.page_analyses_scoring.length : 0;
		const page_data_date = site_analysis.page_data_date ? site_analysis.page_data_date : site_analysis.run_date;
		return (
			<React.Fragment>
				<div className="site-coverage">
					{num_analyzed + ' of ' + site_analysis.num_site_pages + ' pages analyzed'}
				</div>
				<div className="site-analysis-date">
					{'Analysis date ' + format_run_date(page_data_date)}
				</div>
			</React.Fragment>
		);
	};

	const render_headline = ()=>{
		return (
			<div className="site-headline">
				<h1 className="aeo-title aeo-title-compact">{c_Headline}</h1>
				<p className="aeo-description aeo-description-compact">{c_Subheadline}</p>
			</div>
		);
	};

	return (
		<React.Fragment>
			<Container className="site-view">
				{render_headline()}
				<Row className="site-title-row">
					<Col>
						{render_title()}
					</Col>
					<Col className="site-title-actions">
						{render_analyze_button()}
					</Col>
				</Row>

				{error_message && <Alert variant="danger" className="site-view-error">{error_message}</Alert>}
				{pdf_error_message && <Alert variant="danger" className="site-view-error">{pdf_error_message}</Alert>}
				{render_progress()}
				{render_summary()}
				{render_notes()}
				{render_pages()}
			</Container>

			{render_exhausted_modal()}
			<PdfExportModal show={fetching_pdf} stage={pdf_stage}/>
		</React.Fragment>
	);
}

function SiteView(){
	const render_content = (on_analysis_change, on_ask_ai)=>{
		return (
			<SiteViewContent on_analysis_change={on_analysis_change} on_ask_ai={on_ask_ai}/>
		);
	};

	return (
		<AssistantLayout render_content={render_content}/>
	);
}

export default SiteView;
